import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { api } from "../api";
import { confirmAction } from "../confirmAction";
import { bankName, banks, formatAccountNumber } from "../banks";
import { money } from "../employmentPresentation";
import { workerCategoryOf } from "../workerCategory";
import { todayInSeoul } from "../holidayCalendar";
import { matchesKoreanName } from "../finishTabletPresentation";
import type { BootstrapResponse, EmploymentContract, EmploymentContractType, PartTimeWorkRecord, SaveRobotInput, SaveWorkerInput, Worker, WorkerGroup } from "../types";
import { useRfidSerial } from "../useRfidSerial";
import { MultiPicker } from "./MultiPicker";

/* 010-0000-0000 꼴로 하이픈을 대신 넣는다. 서울 국번(02)은 앞자리가 둘이라 나누는 자리가
   다르므로 따로 센다. */
export const formatPhone = (value: string) => {
  const digits = value.replace(/[^0-9]/g, "").slice(0, 11);
  if (digits.startsWith("02")) {
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, digits.length - 4)}-${digits.slice(-4)}`;
  }
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, digits.length - 4)}-${digits.slice(-4)}`;
};

/* 앞 여섯 자리 뒤에 하이픈을 대신 넣는다. 열세 자리를 한 줄로 치면 어디까지 쳤는지 세어야
   하고, 자리표시가 000000-0000000이라 손으로 치는 하이픈이 하나 더 들어가기도 했다. */
const formatResidentNumber = (value: string) => {
  const digits = value.replace(/[^0-9]/g, "").slice(0, 13);
  return digits.length > 6 ? `${digits.slice(0, 6)}-${digits.slice(6)}` : digits;
};
import { useAcknowledgedMutation } from "./useAcknowledgedMutation";
import { Select } from "./Select";
import { ChevronLeft } from "lucide-react";
import { Modal } from "./ui/Modal";
import { RowActions } from "./ui/RowActions";
import { DayChips } from "./ui/DayChips";
import { DayHourRows, restFor, type DayHours } from "./ui/DayHourRows";
import { usePlanningBaseline } from "./ui/DesignSystem";
import { Button } from "./ui/Button";
import { Field, Input, Textarea } from "./ui/Field";
import { Field as FormField, Input as FormInput, Modal as FormModal, Button as FormButton } from "./next-ui/controls";
import "./WorkerProfileNext.css";
import { EmptyState, Feedback, Panel, TableFrame } from "./ui/Surface";
import "./WorkerManagement.css";
import "./EmploymentManagement.css";


/* 공정이 흐르는 순서대로 둔다 — 밑작업으로 시작해 재봉·완성을 거쳐 QC로 끝난다. 화면에
   찍히는 순서가 곧 일이 지나가는 순서여야 어느 단계 사람인지 자리로 읽힌다.
   side_job은 저장된 값이라 그대로 두고 부르는 말만 "밑"으로 바꾼다. */
export const workerGroupLabels: Record<WorkerGroup, string> = {
  side_job: "밑", sewing: "재봉", finish: "완성", qc: "QC", operations: "운영"
};

type Message = { tone: "success" | "error"; text: string } | null;
interface CommonProps { data: BootstrapResponse; refresh: () => Promise<void>; notify: (message: Message) => void; actorName: string; hideTable?: boolean; onReady?: (controls: WorkerControls) => void; }
export type WorkerControls = { openWorker: (worker?: Worker, defaults?: { workGroups?: WorkerGroup[] }) => void; deleteWorker: (worker: Worker) => void; openPrivateInfo: (worker: Worker, at?: DOMRect) => void };

function PrivateField({ revealed, onReveal, label, preview, children, className }: {
  revealed: boolean;
  onReveal: () => void;
  label: string;
  preview: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  if (revealed) return <div className={`studio-private-revealed${className ? ` ${className}` : ""}`}>{children}</div>;
  return <button type="button" className={`studio-private-mask ${className ?? ""}`} aria-label={`${label} 보기`} onClick={onReveal}>
    <span className="studio-private-blurred" aria-hidden="true">{preview}</span>
    <span className="studio-private-hint"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12s3.4-6 9.5-6 9.5 6 9.5 6-3.4 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.7" /></svg>보기</span>
  </button>;
}

const days = ["월", "화", "수", "목", "금", "토", "일"];
const clockMinutes = (value: string) => Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5));

/* 2026-10-19 지윤: 작업자가 날마다 다른 시각에 나온다 — 요일마다 시작·종료를 따로 적는다.
   저장 계약(SaveWorkerInput)은 아직 시작·종료가 하나씩이라, 화면은 요일별로 받아 두고
   보낼 때는 가장 이른 시작과 가장 늦은 종료로 접는다. 서버·플래너가 요일별을 받게 되면
   접는 줄만 걷으면 된다(그때까지 기존 배치 계산은 그대로 돈다). */
const spread = (days: string[], start: string, end: string, rest?: number): Record<string, DayHours> =>
  Object.fromEntries(days.map(day => [day, { start, end, rest: rest ?? restFor(start, end) }]));
/* 휴게도 하나로 접는다 — 요일 가운데 가장 긴 휴게(접힌 시작~끝이 가장 긴 날을 품으므로). */
const foldRest = (days: string[], hours: Record<string, DayHours>, fallback: number) => {
  const picked = days.map(day => hours[day]).filter(Boolean).map(value => value.rest ?? restFor(value.start, value.end));
  return picked.length ? Math.max(...picked) : fallback;
};
const foldHours = (days: string[], hours: Record<string, DayHours>, fallback: DayHours) => {
  const picked = days.map(day => hours[day]).filter(Boolean);
  const value = picked.length
    ? { start: picked.map(v => v.start).sort()[0], end: picked.map(v => v.end).sort().at(-1)! }
    : fallback;
  return { startTime: value.start, endTime: value.end };
};

const time = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
const emptyWorker = (): SaveWorkerInput => ({
  name: "", bankCode: "", accountNumber: "", accountHolder: "", residentRegistrationNumber: "",
  address: "", phone: "", active: true, workGroups: ["sewing"], notes: "",
  workDays: ["월", "화", "수", "목", "금"], startTime: "09:00", endTime: "18:00", breakMinutes: 60, planningPriority: 0, settlementType: "piece", rfidTag: ""
});
const workerInput = (worker: Worker): SaveWorkerInput => ({
  name: worker.name, bankCode: worker.bankCode, accountNumber: formatAccountNumber(worker.accountNumber, worker.bankCode),
  accountHolder: worker.accountHolder, residentRegistrationNumber: "", address: worker.address,
  /* 저장은 숫자만 한다. 열 때 등록 화면과 같은 모양으로 끊어 줘야 같은 칸으로 읽힌다 —
     수정에서만 01087170980이 나오면 다른 화면처럼 보인다. */
  phone: formatPhone(worker.phone), active: worker.active, workGroups: worker.workGroups, notes: worker.notes,
  workDays: worker.workDays, startTime: time(worker.workStartMinutes ?? 540),
  /* 저장된 분은 휴게를 이미 뺀 실근무다. 되돌려 보여줄 때는 휴게를 다시 더해야 퇴근 시각이
     나온다 — 안 더하면 열 때마다 퇴근이 휴게만큼 앞당겨진다. */
  endTime: time((worker.workStartMinutes ?? 540) + Math.max(worker.weekdayMinutes, worker.weekendMinutes) + (worker.breakMinutes ?? 0)),
  breakMinutes: worker.breakMinutes ?? 0,
  planningPriority: worker.planningPriority ?? 0, settlementType: worker.settlementType === "hourly" ? "hourly" : "piece", rfidTag: worker.rfidTag
});
const emptyRobotExtra = () => ({ modelName: "", serialNumber: "", defaultUnitsPerHour: 100 });
/* 보수를 무엇 단위로 주기로 했는지. 구분이 정해 주는 값이라 대부분 고를 일이 없지만,
   기간제(월·주)와 단기알바(시·일)만은 사람마다 달라 고르게 둔다. */
type WagePeriod = "monthly" | "weekly" | "daily" | "hourly" | "contract";
const wagePeriodLabels: Record<WagePeriod, string> = { monthly: "월급", weekly: "주급", daily: "일급", hourly: "시급", contract: "계약급" };
const wagePeriodsByKind: Record<CreateKind, WagePeriod[]> = {
  regular: ["monthly"], piece: ["monthly"], fixed_term: ["monthly", "weekly"],
  part_time: ["hourly", "daily"], freelance: ["contract"], robot: ["monthly"],
};
const emptyContractExtra = () => ({ startDate: todayInSeoul(), endDate: "", wage: "", wagePeriod: "monthly" as WagePeriod });
/* "사람"은 뺐다. 그 선택은 정규와 똑같이 도급으로 저장되면서 고용계약만 남기지 않았고,
   목록에서는 둘 다 "정규"로 표시되어 고르는 것은 둘인데 결과는 하나였다 (workerCategory.ts).
   남는 다섯은 로봇이거나, 고용 형태 넷 중 하나다. */
type CreateKind = "piece" | "part_time" | "robot" | EmploymentContractType;
const isContractKind = (kind: CreateKind): kind is EmploymentContractType => kind === "regular" || kind === "fixed_term" || kind === "freelance";

export function WorkerManagement({ data, refresh, notify, actorName, hideTable = false, onExit, onReady }: CommonProps & { onExit?: () => void }) {
  const baseline = usePlanningBaseline();
  const [privateInfoTarget, setPrivateInfoTarget] = useState<Worker | null>(null);
  const [privateInfoAt, setPrivateInfoAt] = useState<{ top: number; right: number } | null>(null);
  /* 주민번호 전체 — 팝업을 열 때 관리자에게만 받는다(2026-10-20 지윤 「항상 전체로」). 못 받으면 가린 값 그대로. */
  const [residentNumbers, setResidentNumbers] = useState<Record<string, string>>({});
  useEffect(() => {
    /* 열쇠에 가린 값을 같이 둔다 — 번호를 고치면 가린 값도 바뀌어 새로 받는다. */
    const id = privateInfoTarget?.id, masked = privateInfoTarget?.residentRegistrationNumberMasked;
    if (!id || !masked || residentNumbers[`${id}:${masked}`] !== undefined) return;
    let alive = true;
    api.getWorkerResidentRegistrationNumber(id)
      .then(result => { if (alive && result.value) setResidentNumbers(current => ({ ...current, [`${id}:${masked}`]: result.value })); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [privateInfoTarget?.id, privateInfoTarget?.residentRegistrationNumberMasked]);
  const editorOpener = useRef<{ element: HTMLElement | null; workerId?: string }>({ element: null });
  const [editing, setEditing] = useState<Worker | "new" | null>(null);
  const [deleting, setDeleting] = useState<Worker | null>(null);
  const [draft, setDraft] = useState<SaveWorkerInput>(emptyWorker());
  /* 요일별 근무 시간 — 화면이 들고 있다가 저장할 때 하나로 접는다(위 foldHours 주석). */
  const [dayHours, setDayHours] = useState<Record<string, DayHours>>(() => spread(["월", "화", "수", "목", "금"], "09:00", "18:00"));
  /* 목록의 첫 줄이 곧 기본값이다 — 로봇이 맨 위에 서 있는데 도급제가 골라져 있으면
     고르개가 제 목록과 어긋난다. */
  /* 구분 목록은 로봇·사람·업무위탁 셋이다(e3c30fe9) — 기본값도 그 안의 「사람」. 「regular」로 두었더니 목록에 없는 값이라
     "이전 선택 · 항목 없음"(빨강)으로 떴다. */
  const [createKind, setCreateKind] = useState<CreateKind>(baseline ? "piece" : "robot");
  const [robotExtra, setRobotExtra] = useState(emptyRobotExtra());
  const [contractExtra, setContractExtra] = useState(emptyContractExtra());
  /* 제출을 한 번이라도 눌렀을 때만 빨간 표시를 낸다 — 채우기 전부터 온통 빨갛게 두면
     아직 손대지 않은 칸까지 잘못된 것처럼 보인다. */
  const [attempts, setAttempts] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const [workerSearch, setWorkerSearch] = useState("");
  const [workGroupFilter, setWorkGroupFilter] = useState<WorkerGroup | "all">("all");
  const [revealedPrivateFields, setRevealedPrivateFields] = useState<Set<string>>(() => new Set());
  const [identityPhoto, setIdentityPhoto] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalMessage, setModalMessage] = useState<Message>(null);
  const [identityPhotoUrls, setIdentityPhotoUrls] = useState<Record<string, string>>({});
  const identityPhotoUrlsRef = useRef<Record<string, string>>({});
  const [photoErrors, setPhotoErrors] = useState<Set<string>>(() => new Set());
  const photosLoading = useRef(new Set<string>());
  const mounted = useRef(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  /* 등록이든 수정이든 같은 규칙을 쓴다. 등록에서는 고른 구분이, 수정에서는 이미 정해진
     구분이 답이다 — 화면마다 다른 규칙을 두면 같은 사람이 어느 문으로 들어왔느냐에 따라
     다른 칸을 보게 된다. 수정의 구분은 표와 같은 곳(workerCategory)에서 온다. */
  const kind: CreateKind = createKind;
  const isRobotDraft = kind === "robot";
  /* 구분을 바꾸면 고를 수 있는 주기가 통째로 갈린다. 상태를 따라 고쳐 쓰는 대신 여기서
     걸러 내면, 구분을 오가다 "정규직인데 시급"처럼 남은 값이 저장될 일이 없다. */
  /* 종료일이 있을 수 있는지도 구분이 정한다. 정규직·도급제는 끝을 정하지 않고, 기간제는
     끝이 있다는 것 자체가 정의라 비울 수가 없다. 남는 둘만 비어도 되는 칸이다. */
  const endDateMode = kind === "regular" || kind === "piece" ? "none"
    : kind === "fixed_term" ? "required" : "optional";
  const hasContract = !isRobotDraft && (!baseline || kind !== "piece");
  const wagePeriodChoices = wagePeriodsByKind[kind];
  const wagePeriod = wagePeriodChoices.includes(contractExtra.wagePeriod) ? contractExtra.wagePeriod : wagePeriodChoices[0];
  /* 단기알바는 요일·시간이 매번 다르다. 그 값을 여기서 받으면 한 번 정한 패턴이 사실처럼
     표에 찍히는데(남지호: 월·화·목·금·토 09:00–16:30), 실제로는 하루 일한 사람이다.
     언제 얼마나 일했는지는 건별 근무기록이 맡는다 — 배치 대상도 아니라 쓰는 곳도 없다. */
  const isPartTimeDraft = editing === "new" ? createKind === "part_time" : editing !== null && editing.settlementType === "hourly";
  const rfidSerial = useRfidSerial((tag) => {
    setDraft((current) => ({ ...current, rfidTag: tag }));
  }, Boolean(editing && !isRobotDraft));
  const rfidSerialLabel = rfidSerial.state === "connected" ? "RFID 연결됨 · 카드를 태그하세요"
    : rfidSerial.state === "connecting" ? "RFID 연결 중"
      /* 이름 줄에 들어가야 하므로 짧아야 한다. 긴 문장은 잘려서 끝이 안 보이는데, 잘린
         안내는 없는 것보다 나쁘다 — 무슨 말인지 모른 채 자리만 차지한다. */
      : rfidSerial.state === "unsupported" ? "Chrome·Edge만 지원"
        : rfidSerial.state === "error" ? rfidSerial.message || "RFID 연결 오류"
          : "RFID 리더기를 연결하면 태그가 자동 입력됩니다";
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      Object.values(identityPhotoUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
      identityPhotoUrlsRef.current = {};
    };
  }, []);
  /* 어느 구획의 +를 눌렀는지가 곧 그 사람의 작업군이다 — 플래너에서 들어오면 그 값을
     미리 채워, 작업군을 다시 고르는 일을 없앤다. */
  const open = (worker?: Worker, defaults?: { workGroups?: WorkerGroup[] }) => {
    editorOpener.current = { element: document.activeElement instanceof HTMLElement ? document.activeElement : null, workerId: worker?.id };
    setEditing(worker ?? "new");
    /* 예금주는 사람이 직접 친다(2026-10-19 밤 지윤) — 이름으로 미리 채우지 않는다. */
    const base = worker ? workerInput(worker) : { ...emptyWorker(), ...(defaults?.workGroups?.length ? { workGroups: defaults.workGroups } : {}) };
    setDraft(base);
    /* 요일별 시각·휴게를 그 작업자 값으로 편다 — 안 펴면 수정 창이 늘 9~18시로 열리고 저장하면 덮였다. */
    setDayHours(spread(base.workDays, base.startTime, base.endTime, base.breakMinutes));
    setCreateKind(worker ? (worker.kind === "robot" ? "robot" : workerCategoryOf(data)(worker)) : baseline ? "piece" : "robot");
    /* 로봇을 수정으로 열면 모델·식별번호·생산량이 그대로 나와야 한다. 빈 값으로 열리면
       저장할 때마다 적어 둔 것이 지워진다. */
    const robot = worker?.kind === "robot" ? (data.robots ?? []).find((item) => item.workerId === worker.id) : undefined;
    setRobotExtra(robot
      ? { modelName: robot.modelName ?? "", serialNumber: robot.serialNumber ?? "", defaultUnitsPerHour: robot.defaultUnitsPerHour ?? 100 }
      : emptyRobotExtra());
    /* 수정으로 열면 계약 줄이 지금 계약을 그대로 들고 나와야 한다. 빈 칸으로 열리면
       저장할 때마다 계약이 오늘 날짜로 새로 시작한 것처럼 덮인다. */
    const contract = worker ? data.employmentContracts.find((item) => item.workerId === worker.id && item.active) : undefined;
    setContractExtra(contract
      ? { startDate: contract.startDate, endDate: contract.endDate ?? "", wage: String(contract.monthlyWage || ""), wagePeriod: contract.wagePeriod }
      : emptyContractExtra());
    setIdentityPhoto(null);
    setModalMessage(null);
    setAttempts(0);
  };
  const closeEditor = () => { setEditing(null); setModalMessage(null); setIdentityPhoto(null); };
  const humanWorkers = useMemo(() => data.workers.filter((worker) => worker.kind === "human"), [data.workers]);
  const filteredWorkers = useMemo(() => {
    const query = workerSearch.trim();
    return humanWorkers.filter((worker) =>
      (matchesKoreanName(worker.name, query) || matchesKoreanName(worker.displayedName, query)) &&
      (workGroupFilter === "all" || worker.workGroups.includes(workGroupFilter))
    ).sort((left, right) => Number(right.active) - Number(left.active) || left.displayedName.localeCompare(right.displayedName, "ko-KR"));
  }, [humanWorkers, workerSearch, workGroupFilter]);
  const isPrivateFieldRevealed = (workerId: string, field: string) => revealedPrivateFields.has(`${workerId}:${field}`);
  const revealPrivateField = (workerId: string, field: string) => setRevealedPrivateFields((current) => {
    const next = new Set(current);
    next.add(`${workerId}:${field}`);
    return next;
  });
  const revealIdentityPhotos = async (worker: Worker) => {
    if (photosLoading.current.has(worker.id)) return;
    photosLoading.current.add(worker.id);
    revealPrivateField(worker.id, "identity-photo");
    setPhotoErrors((current) => { const next = new Set(current); next.delete(worker.id); return next; });
    const photos = worker.identityPhotos.length ? worker.identityPhotos : worker.identityPhoto ? [worker.identityPhoto] : [];
    const missing = photos.filter((photo) => !identityPhotoUrlsRef.current[photo.id]);
    const results = await Promise.allSettled(missing.map(async (photo) => [photo.id, await api.loadWorkerIdentityPhoto(worker.id, photo.id)] as const));
    photosLoading.current.delete(worker.id);
    for (const result of results) {
      if (result.status !== "fulfilled") continue;
      const [id, url] = result.value;
      if (!mounted.current) URL.revokeObjectURL(url);
      else identityPhotoUrlsRef.current[id] = url;
    }
    if (mounted.current) {
      identityPhotoUrlsRef.current = { ...identityPhotoUrlsRef.current };
      setIdentityPhotoUrls(identityPhotoUrlsRef.current);
      if (results.some((result) => result.status === "rejected")) setPhotoErrors((current) => new Set(current).add(worker.id));
    }
  };
  const existingContract = editing && editing !== "new" ? data.employmentContracts.find((contract) => contract.workerId === editing.id && contract.active) : undefined;
  /**
   * 빈 칸을 한 번에 모은다. 예전에는 첫 번째 문제에서 멈추고 팝업을 띄웠는데, 열 칸이 비어
   * 있으면 열 번을 제출해야 했고 어느 칸이 문제인지는 글로 설명해야 했다. 칸 옆에 직접
   * 표시하면 어디가 잘못됐는지 화면이 가리킨다.
   */
  const existing = editing && editing !== "new" ? editing : null;
  const collectErrors = () => {
    const errors: Record<string, string> = {};
    if (!draft.name.trim()) errors.name = "이름을 입력해 주세요.";
    if (!draft.workDays.length) errors.workDays = "근무 요일을 한 개 이상 선택해 주세요.";
    /* 요일별 줄이 실제 값이다 — 접은 시작·끝으로 검사한다. */
    const folded = foldHours(draft.workDays, dayHours, { start: draft.startTime, end: draft.endTime });
    if (folded.endTime <= folded.startTime) errors.endTime = "근무 종료는 시작보다 늦어야 합니다.";
    /* 휴게를 뺀 실근무가 남아야 한다 — 요일마다 본다(09:00~10:00에 휴게 90분이면 계획이 음수 분을 받는다). */
    else if (!isRobotDraft && draft.workDays.some(day => { const value = dayHours[day]; if (!value) return false;
      return clockMinutes(value.end) - clockMinutes(value.start) - (value.rest ?? restFor(value.start, value.end)) <= 0; }))
      errors.breakMinutes = "휴게가 근무 시간보다 길거나 같은 요일이 있습니다.";
    if (isRobotDraft) {
      if (!robotExtra.defaultUnitsPerHour || robotExtra.defaultUnitsPerHour <= 0) errors.defaultUnitsPerHour = "기본 시간당 생산량을 입력해 주세요.";
      return errors;
    }
    /* 수정에서는 원래 비어 있던 칸을 새로 강제하지 않는다. 등록 규칙을 그대로 적용했더니
       주소도 주민번호도 없이 들어온 기존 28명은 이름 한 글자를 고치려 해도 그 둘을 먼저
       받아내야 했다 — 고치기를 포기하게 만드는 문이 된다. 채워져 있던 칸을 지우는 것만
       막는다. */
    const wasFilled = (had: boolean, value: string) => !existing || had ? !value.trim() : false;
    if (wasFilled(Boolean(existing?.phone), draft.phone)) errors.phone = "연락처를 입력해 주세요.";
    if (wasFilled(Boolean(existing?.address), draft.address)) errors.address = "주소를 입력해 주세요.";
    if (wasFilled(Boolean(existing?.bankCode), draft.bankCode)) errors.bankCode = "은행을 선택해 주세요.";
    if (wasFilled(Boolean(existing?.accountNumber), draft.accountNumber)) errors.accountNumber = "계좌번호를 입력해 주세요.";
    if (!draft.accountHolder.trim() && !isRobotDraft) errors.accountHolder = "예금주명을 입력해 주세요.";
    if (!existing && !draft.residentRegistrationNumber) errors.residentRegistrationNumber = "주민등록번호를 입력해 주세요.";
    if (!existing && !identityPhoto) errors.identityPhoto = "신분증 사진을 첨부해 주세요.";
    if (!draft.workGroups.length) errors.workGroups = "작업군을 한 개 이상 선택해 주세요.";
    if (!isRobotDraft && (isContractKind(kind) || kind === "part_time")) {
      if (!contractExtra.startDate) errors.startDate = "계약 시작일을 입력해 주세요.";
      if (endDateMode === "required" && !contractExtra.endDate) errors.endDate = "계약 종료일을 입력해 주세요.";
      if (!Number(contractExtra.wage)) errors.wage = "보수 금액을 입력해 주세요.";
    }
    return errors;
  };
  const errors: Record<string, string> = attempts ? collectErrors() : {};
  /* 첫 잘못된 칸으로 화면을 데려간다. 창이 길어 아래쪽 칸이 화면 밖일 때, 빨갛게 칠해 두기만
     하면 눌러도 아무 일도 안 일어난 것처럼 보인다. */
  useEffect(() => {
    if (!attempts) return;
    const target = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"], [data-invalid] input, [data-invalid] button');
    target?.focus({ preventScroll: true });
    target?.closest(".ui-field, fieldset")?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [attempts]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setModalMessage(null);
    setAttempts((value) => value + 1);
    /* 표시만 하고 멈춘다. 어느 칸인지는 그 칸이 스스로 말한다. */
    if (Object.keys(collectErrors()).length) return;
    const existing = editing === "new" ? null : editing;
    /* 계약은 로봇만 빼고 누구에게나 만든다. 단기알바도 하루짜리 계약(시작일 = 종료일)으로
       적히므로 같은 뷰가 성립한다. 도급제는 계약 종류가 따로 없어 "정규직"으로 저장되지
       않도록 계약을 만들지 않는다 — 구분은 계약 유무로 갈린다 (workerCategory.ts). */
    /* 계약은 계약을 맺는 구분에만 만든다. 도급제는 계약이 없다는 것 자체가 구분이므로
       (workerCategory.ts) 여기서 계약을 만들면 표에서 정규직으로 뒤집힌다. */
    const wantsContract = isContractKind(kind) || kind === "part_time";
    if (isRobotDraft) {
      setBusy(true);
      try {
        const robotInput: SaveRobotInput = {
          workGroups: draft.workGroups, name: draft.name, modelName: robotExtra.modelName, serialNumber: robotExtra.serialNumber,
          defaultUnitsPerHour: robotExtra.defaultUnitsPerHour, active: draft.active,
          workDays: draft.workDays, ...foldHours(draft.workDays, dayHours, { start: draft.startTime, end: draft.endTime }),
          planningPriority: draft.planningPriority, notes: draft.notes
        };
        // Editing a robot must not create a duplicate, regardless of its theme.
        const existingRobot = existing ? (data.robots ?? []).find(item => item.workerId === existing.id) : undefined;
        if (existingRobot) await api.updateRobot(existingRobot.id, robotInput);
        else await api.createRobot(robotInput);
        await refresh(); closeEditor();
      } catch (error) { setModalMessage({ tone: "error", text: error instanceof Error ? error.message : "로봇 정보를 저장하지 못했습니다." }); }
      finally { setBusy(false); }
      return;
    }
    setBusy(true);
    try {
      /* 요일별 줄을 접어 보낸다 — 전에는 로봇·계약만 접고 사람 작업자는 처음 값(9~18시)을 그대로 보냈다. */
      const input = { ...draft, ...foldHours(draft.workDays, dayHours, { start: draft.startTime, end: draft.endTime }),
        breakMinutes: foldRest(draft.workDays, dayHours, draft.breakMinutes), accountHolder: draft.accountHolder.trim() };
      let createdWorkerId: string | null = null;
      if (editing === "new") createdWorkerId = (await api.createWorker(input, identityPhoto!)).id;
      else if (editing) await api.updateWorker(editing.id, input, identityPhoto ?? undefined);
      if (identityPhoto && editing !== "new" && editing) {
        setRevealedPrivateFields((current) => { const next = new Set(current); next.delete(`${editing.id}:identity-photo`); return next; });
      }
      const contractWorkerId = createdWorkerId ?? existing?.id ?? null;
      if (wantsContract && contractWorkerId) {
        try {
          const contractInput = {
            workerId: contractWorkerId, contractType: (isContractKind(kind) ? kind : "fixed_term") as EmploymentContractType, startDate: contractExtra.startDate,
            endDate: endDateMode === "none" ? null : contractExtra.endDate || null, workDays: draft.workDays, ...foldHours(draft.workDays, dayHours, { start: draft.startTime, end: draft.endTime }),
            /* 계약에는 금액을 적지 않는다. 돈은 정산이 건별로 기록한다. */
            monthlyWage: Number(contractExtra.wage.replace(/\D/g, "")) || 0, wagePeriod, memo: existingContract?.memo ?? "", active: draft.active, createdBy: actorName
          };
          if (existingContract) await api.updateEmploymentContract(existingContract.id, contractInput);
          else await api.createEmploymentContract(contractInput);
        } catch (contractError) {
          await refresh(); closeEditor();
          notify({ tone: "error", text: `작업자는 등록됐지만 근무 계약 등록에는 실패했습니다: ${contractError instanceof Error ? contractError.message : "다시 시도해 주세요."} 계약은 목록에서 다시 등록해 주세요.` });
          return;
        }
      }
      /* 도급제로 바꿨으면 있던 계약을 끝낸다. 계약이 없다는 것 자체가 도급제이므로
         (workerCategory.ts) 계약을 살려 두면 표에서는 여전히 정규직으로 읽힌다. 지우지
         않고 종료로 남기는 건 언제까지 그 조건이었는지가 정산의 근거이기 때문이다. */
      if (!wantsContract && existingContract) {
        await api.updateEmploymentContract(existingContract.id, {
          workerId: existingContract.workerId, contractType: existingContract.contractType,
          startDate: existingContract.startDate, endDate: existingContract.endDate,
          workDays: existingContract.workDays, startTime: existingContract.startTime, endTime: existingContract.endTime,
          monthlyWage: existingContract.monthlyWage, wagePeriod: existingContract.wagePeriod,
          memo: existingContract.memo, active: false, createdBy: actorName
        });
      }
      /* 잘된 일은 알리지 않는다. 창이 닫히고 표가 바뀐 것이 이미 답이고, 띠가 뜨면 그것을
         또 지워야 한다. 알림은 실패했을 때만 남는다. */
      await refresh(); closeEditor();
    } catch (error) { setModalMessage({ tone: "error", text: error instanceof Error ? error.message : "작업자 정보를 저장하지 못했습니다." }); }
    finally { setBusy(false); }
  };
  const openPrivateInfo = (worker: Worker, at?: DOMRect) => {
    setPrivateInfoTarget(worker);
    setPrivateInfoAt(baseline && at ? { top: Math.min(at.top, window.innerHeight - 160), right: Math.max(8, window.innerWidth - at.left + 8) } : null);
    if (worker.identityPhoto) void revealIdentityPhotos(worker);
  };
  useEffect(() => {
    onReady?.({ openWorker: open, deleteWorker: requestDelete, openPrivateInfo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onReady]);
  const requestDelete = (worker: Worker) => {
    if (baseline) {
      if (confirmAction(`${worker.displayedName} 작업자를 삭제하시겠습니까?`, "작업자 목록과 이후 플래닝 대상에서는 제외되며, 완료한 작업·급여·정산 기록은 그대로 보존됩니다.")) void confirmDelete(worker);
    } else { setModalMessage(null); setDeleting(worker); }
  };
  const confirmDelete = async (worker: Worker | null = deleting) => {
    if (!worker || busy) return;
    setBusy(true); setModalMessage(null);
    try {
      await api.deleteWorker(worker.id);
      await refresh(); setDeleting(null); notify({ tone: "success", text: `${worker.displayedName} 작업자를 삭제했습니다.` });
    } catch (error) {
      const message = { tone: "error" as const, text: error instanceof Error ? error.message : "작업자를 삭제하지 못했습니다." };
      if (baseline) notify(message); else setModalMessage(message);
    }
    finally { setBusy(false); }
  };
  return <>
    {!hideTable && <Panel title={<>작업자 <span className="worker-count">전체 {humanWorkers.length}명 · 활성 {humanWorkers.filter((item) => item.active).length}명</span></>}
      className="worker-management" actions={<Button variant="primary" onClick={() => open()}>작업자 등록</Button>}>
      <div className="worker-filters">
        <Field label="작업군"><Select aria-label="작업군 필터" value={workGroupFilter} onChange={(event) => setWorkGroupFilter(event.target.value as WorkerGroup | "all")}><option value="all">전체 작업군</option>{Object.entries(workerGroupLabels).map(([group, label]) => <option key={group} value={group}>{label}</option>)}</Select></Field>
        <span className="worker-filter-count" role="status">검색 결과 <strong>{filteredWorkers.length}</strong>명</span>
      </div>
      <TableFrame label="작업자 목록" tableClassName="worker-basic-table">
        <thead><tr><th scope="col"><span className="worker-name-header"><span>이름</span><Input type="search" aria-label="작업자 이름 검색" placeholder="이름·초성 검색" value={workerSearch} onChange={(event) => setWorkerSearch(event.target.value)} /></span></th><th scope="col">작업군</th><th scope="col">연락처</th><th scope="col">계좌 정보</th><th scope="col">주민등록번호</th><th scope="col">신분증</th><th scope="col">상태</th><th scope="col">관리</th></tr></thead>
        <tbody>{filteredWorkers.map((worker) => <tr key={worker.id} className={`worker-row${!worker.active ? " inactive" : ""}`}>
          <td><strong>{worker.displayedName}</strong></td>
          <td><div className="worker-group-badges">{worker.workGroups.map((group) => <span key={group}>{workerGroupLabels[group]}</span>)}</div></td>
          <td><PrivateField revealed={isPrivateFieldRevealed(worker.id, "phone")} onReveal={() => revealPrivateField(worker.id, "phone")} label={`${worker.displayedName} 연락처`} preview={worker.phone || "미입력"}>{worker.phone || <span className="worker-missing">미입력</span>}</PrivateField></td>
          <td><PrivateField revealed={isPrivateFieldRevealed(worker.id, "account")} onReveal={() => revealPrivateField(worker.id, "account")} label={`${worker.displayedName} 계좌 정보`} preview={<><strong>{worker.bankCode ? bankName(worker.bankCode) : "은행 미입력"} · {worker.accountNumber || "계좌 미입력"}</strong><small>{worker.accountHolder || "예금주 미입력"}</small></>}><strong>{worker.bankCode ? bankName(worker.bankCode) : "은행 미입력"} · {worker.accountNumber || "계좌 미입력"}</strong><small className={worker.accountHolder.trim() !== worker.name.trim() ? "studio-account-holder-mismatch" : undefined}>{worker.accountHolder || "예금주 미입력"}</small></PrivateField></td>
          <td><PrivateField revealed={isPrivateFieldRevealed(worker.id, "resident-number")} onReveal={() => revealPrivateField(worker.id, "resident-number")} label={`${worker.displayedName} 주민등록번호`} preview={worker.residentRegistrationNumberMasked || "미입력"}>{worker.residentRegistrationNumberMasked || <span className="worker-missing">미입력</span>}</PrivateField></td>
          <td>{worker.identityPhoto ? <PrivateField className="studio-private-photo" revealed={isPrivateFieldRevealed(worker.id, "identity-photo")} onReveal={() => void revealIdentityPhotos(worker)} label={`${worker.displayedName} 신분증 사진`} preview={`사진 ${worker.identityPhotos.length || 1}장`}>
            <div className="studio-photo-thumbnails">{(worker.identityPhotos.length ? worker.identityPhotos : [worker.identityPhoto]).map((photo, index) => identityPhotoUrls[photo.id]
              ? <img src={identityPhotoUrls[photo.id]} alt={`${worker.displayedName} 신분증 ${index + 1}`} key={photo.id} />
              : <span className="worker-photo-placeholder" role={photoErrors.has(worker.id) ? undefined : "status"} key={photo.id}>{photoErrors.has(worker.id) ? <><span>사진을 불러오지 못했습니다.</span><Button size="compact" onClick={() => void revealIdentityPhotos(worker)}>다시 시도</Button></> : "사진 불러오는 중…"}</span>)}</div>
          </PrivateField> : <span className="worker-missing">미등록</span>}</td>
          <td><span className={`worker-status${worker.active ? " worker-status--active" : ""}`}>{worker.active ? "활성" : "비활성"}</span></td>
          {/* 2026-10-19 지윤 지시: 줄 끝 손잡이는 저쪽 설정 창 표처럼 ⋮ 하나다 — 단추 둘을
              늘어놓으면 그 폭을 줄마다 비워 둬야 하고 좁은 창에서 먼저 찌그러진다. */}
          <td><RowActions label={worker.displayedName} actions={[
            { label: "수정", onSelect: () => open(worker) },
            { label: "삭제", onSelect: () => requestDelete(worker), danger: true },
          ]} /></td>
        </tr>)}</tbody>
      </TableFrame>
      {!filteredWorkers.length && <EmptyState>{humanWorkers.length ? "조건에 맞는 작업자가 없습니다." : "등록된 작업자가 없습니다."}</EmptyState>}
    </Panel>}
    {/* 읽고 닫으면 끝나는 창이라 ✕ 하나면 된다 — 뒤로가기는 화면을 갈아끼웠을 때 돌아갈
        곳이 있다는 뜻인데, 여기서는 뒤 목록이 그대로 보이고 있다. */}
    {privateInfoTarget && <Modal busy={busy} className={`worker-private-dialog${baseline ? " ui-dialog--bare ui-dialog--over" : ""}`} style={baseline && privateInfoAt ? { ...privateInfoAt, left: "auto", bottom: "auto", margin: 0 } : undefined} title={`${privateInfoTarget.displayedName} 개인정보`} onClose={() => { setPrivateInfoTarget(null); setPrivateInfoAt(null); }}>
      <div className="worker-private-content">
        <dl>
          {/* 줄 차례(2026-10-22 지윤): 연락처·주소 → 계좌 정보(은행 · 계좌번호 · 예금주 한 줄) → 주민등록번호·신분증. 이름 줄은 두지 않는다 — 창 제목(「○○ 개인정보」)이 이미 누구인지 말한다.
              한 줄에 정보 하나 — 예금주를 계좌 줄 끝에 회색으로 붙이지 않는다. 예금주가 작업자 이름과 다르면 붉게 알린다(작업자 표와 같은 경고). */}
          <div><dt>연락처</dt><dd>{privateInfoTarget.phone || "미입력"}</dd></div>
          <div><dt>주소</dt><dd>{privateInfoTarget.address || "미입력"}</dd></div>
          {/* 계좌와 예금주는 한 줄(2026-10-22 지윤 「한 줄이어야 하는 거 아냐」) — 한 계좌의 한 사실이다. 다른 값과 같은 색으로 「은행 · 계좌번호 · 예금주」,
              예금주만 작업자 이름과 다르면 붉게 알린다(작업자 표와 같은 경고). */}
          <div><dt>계좌 정보</dt><dd>{privateInfoTarget.bankCode ? bankName(privateInfoTarget.bankCode) : "은행 미입력"} · {privateInfoTarget.accountNumber || "계좌 미입력"} · <span className={privateInfoTarget.accountHolder.trim() && privateInfoTarget.accountHolder.trim() !== privateInfoTarget.name.trim() ? "studio-account-holder-mismatch" : undefined}>{privateInfoTarget.accountHolder || "예금주 미입력"}</span></dd></div>
          <div><dt>주민등록번호</dt><dd>{residentNumbers[`${privateInfoTarget.id}:${privateInfoTarget.residentRegistrationNumberMasked}`] || privateInfoTarget.residentRegistrationNumberMasked || "미입력"}</dd></div>
          {/* 신분증도 네 항목과 같은 사실 하나다. 사진이 있으면 값 자리에 사진이 오고, 없으면
              주민등록번호의 "미입력"과 같은 자리에 "미등록"이 온다. */}
          <div><dt>신분증</dt><dd>{privateInfoTarget.identityPhoto ? <span className="studio-photo-thumbnails">{(privateInfoTarget.identityPhotos.length ? privateInfoTarget.identityPhotos : [privateInfoTarget.identityPhoto]).map((photo, index) => identityPhotoUrls[photo.id]
          ? <img src={identityPhotoUrls[photo.id]} alt={`${privateInfoTarget.displayedName} 신분증 ${index + 1}`} key={photo.id} />
          : <span className="worker-photo-placeholder" role={photoErrors.has(privateInfoTarget.id) ? undefined : "status"} key={photo.id}>{photoErrors.has(privateInfoTarget.id) ? <><span>사진을 불러오지 못했습니다.</span><Button size="compact" onClick={() => void revealIdentityPhotos(privateInfoTarget)}>다시 시도</Button></> : "사진 불러오는 중…"}</span>)}</span> : "미등록"}</dd></div>
        </dl>
      </div>
    </Modal>}
    {editing && <FormModal restoreFocus={() => {
      // A viewport resize can replace roster rows with cards while the editor is
      // open. Restore the logical opener rather than a detached old DOM node.
      const { element, workerId } = editorOpener.current;
      return workerId ? document.querySelector<HTMLButtonElement>(`[data-worker-editor="${CSS.escape(workerId)}"]`) ?? element : element;
    }} onBack={onExit ? closeEditor : undefined} busy={busy} className="worker-profile-dialog" title={editing === "new" ? "작업자 등록" : "작업자 정보 수정"} onClose={() => { closeEditor(); onExit?.(); }}>
      <div className="worker-profile-content">
        {/* 설정 창 안에서는 제목이 머리 띠가 아니라 내용 첫 줄에 선다 — claude.ai 설정 페이지 제목과 같은 자리
            (rail-reference.json `settingsPage`). 머리 띠의 h2 는 화면 읽기용으로 남기고 눈에는 이것만 보인다. */}
        <div className="worker-profile-page-title">
          {onExit && <button type="button" className="worker-profile-page-back" aria-label="뒤로" disabled={busy} onClick={closeEditor}><ChevronLeft size={20} aria-hidden="true" /></button>}
          <span aria-hidden="true">{editing === "new" ? "작업자 등록" : "작업자 정보 수정"}</span>
        </div>
        {modalMessage && <div className="worker-profile-notice"><Feedback tone={modalMessage.tone}>{modalMessage.text}</Feedback></div>}
        {/* 브라우저 기본 검증을 끈다. 켜 두면 첫 빈 칸에서 브라우저 말풍선이 뜨고 submit이
              아예 오지 않아, 칸마다 표시하는 우리 검사가 돌 기회가 없다. */}
        <form ref={formRef} noValidate className="worker-profile-form" onSubmit={submit} aria-busy={busy}>
          <fieldset disabled={busy} className="worker-profile-fields">
            <section className="worker-profile-section" aria-label="기본 정보">
              <div className="worker-profile-grid worker-identity-grid" data-human={!isRobotDraft}>
                {/* 구분이 맨 앞이다. 등록이든 수정이든 이 칸이 아래 줄들의 규칙(종료일·보수)을
                    정하므로 나중에 나오면 이미 채운 칸이 뒤늦게 바뀐다. 수정에서도 고칠 수
                    있어야 한다 — 기간제로 들어온 분이 정규직이 되는 일이 실제로 있다. */}
                {/* 2026-10-19 밤 지윤: 구분·계약·작업군·배치 우선순위는 한 줄 — 넷이 길게 늘어서면 헷갈린다.
                    한 이름 아래 넷이 서니 값만 보고도 무슨 칸인지 읽히게 옵션 글자에 칸 이름을 붙인다. */}
                {isRobotDraft ? <div className="ui-field worker-col-full worker-person-row" role="group" aria-labelledby="worker-person-label">
                  {/* 로봇은 이름 하나와 식별번호 — 모델 칸은 걷었다(이름이 곧 그 로봇을 가리킨다, 2026-10-19 밤 지윤). 저장된 모델 값은 그대로 둔다. */}
                  <label id="worker-person-label">기본 정보</label>
                  <div className="worker-person-controls worker-class-controls">
                    <Select aria-label="작업자 구분" value={kind} disabled={baseline && editing !== "new"} onChange={(e) => {
                      const next = e.target.value as CreateKind; setCreateKind(next);
                      if (next !== "robot") setDraft((current) => next === "part_time"
                        ? { ...current, settlementType: "hourly" }
                        : { ...current, settlementType: "piece" });
                    }}>
                      {/* 로봇이 먼저, 그 다음이 사람의 고용 형태다. 단기알바만 시간으로 정산하고
                          나머지 셋은 개수로 정산한다 — 그 차이는 settlementType이 들고 있다.
                          사람과 로봇은 서로 바꿀 수 없으므로 수정에서는 제 쪽 항목만 남긴다 —
                          로봇을 고치는데 고용 형태가 늘어서 있으면 고를 수 있는 것처럼 보인다. */}
                      {(baseline || editing === "new" || isRobotDraft) && <option value="robot">로봇</option>}
                      {/* 고를 것은 셋이다 — 로봇, 사람, 업무위탁. 이 공장에서 사람은 거의 전부
                          개수로 받으므로 "사람"이 곧 그 방식이고, 정규직·기간제·단기알바를 늘어
                          놓으면 고를 일 없는 칸이 넷 더 선다. 이미 등록된 분들의 구분은 계약이
                          말해 주므로 표와 기록에서는 그대로 보인다. */}
                      {(baseline || !isRobotDraft) && <>
                        <option value="piece">사람</option>
                        <option value="freelance">업무위탁</option>
                      </>}
                    </Select>
                    <FormInput aria-label="이름" aria-invalid={errors.name ? true : undefined} required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="이름" />
                    <FormInput aria-label="식별번호" value={robotExtra.serialNumber} onChange={(e) => setRobotExtra({ ...robotExtra, serialNumber: e.target.value })} placeholder="식별번호" />
                  </div>
                  {errors.name && <span className="ui-field-error" role="alert">{errors.name}</span>}
                </div> : <div className="ui-field worker-col-full worker-person-row" role="group" aria-labelledby="worker-person-label">
                  {/* 2026-10-19 밤 지윤: 이름·연락처·주소는 「기본 정보」 한 줄 — 「계좌」 줄과 같은 짜임. 로봇도 같은 이름의 줄이다. */}
                  <label id="worker-person-label">기본 정보</label>
                  <div className="worker-person-controls worker-class-controls">
                    <Select aria-label="작업자 구분" value={kind} disabled={baseline && editing !== "new"} onChange={(e) => {
                      const next = e.target.value as CreateKind; setCreateKind(next);
                      if (next !== "robot") setDraft((current) => next === "part_time"
                        ? { ...current, settlementType: "hourly" }
                        : { ...current, settlementType: "piece" });
                    }}>
                      {/* 로봇이 먼저, 그 다음이 사람의 고용 형태다. 단기알바만 시간으로 정산하고
                          나머지 셋은 개수로 정산한다 — 그 차이는 settlementType이 들고 있다.
                          사람과 로봇은 서로 바꿀 수 없으므로 수정에서는 제 쪽 항목만 남긴다 —
                          로봇을 고치는데 고용 형태가 늘어서 있으면 고를 수 있는 것처럼 보인다. */}
                      {(baseline || editing === "new" || isRobotDraft) && <option value="robot">로봇</option>}
                      {/* 고를 것은 셋이다 — 로봇, 사람, 업무위탁. 이 공장에서 사람은 거의 전부
                          개수로 받으므로 "사람"이 곧 그 방식이고, 정규직·기간제·단기알바를 늘어
                          놓으면 고를 일 없는 칸이 넷 더 선다. 이미 등록된 분들의 구분은 계약이
                          말해 주므로 표와 기록에서는 그대로 보인다. */}
                      {(baseline || !isRobotDraft) && <>
                        <option value="piece">사람</option>
                        <option value="freelance">업무위탁</option>
                      </>}
                    </Select>
                    <FormInput aria-label="이름" aria-invalid={errors.name ? true : undefined} required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="이름" />
                    <FormInput aria-label="연락처" aria-invalid={errors.phone ? true : undefined} required inputMode="tel" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: formatPhone(e.target.value) })} placeholder="010-0000-0000" />
                    <FormInput aria-label="주소" aria-invalid={errors.address ? true : undefined} required value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} placeholder="주소 입력" />
                  </div>
                  {(errors.name || errors.phone || errors.address) && <span className="ui-field-error" role="alert">{errors.name ?? errors.phone ?? errors.address}</span>}
                </div>}
                {/* 2026-10-19 밤 지윤: 구분은 「기본 정보」 줄 맨 앞, 계약·작업군·배치 우선순위는 「작업」 줄. */}
                <div className="ui-field worker-col-full worker-class-row worker-work-row" role="group" aria-labelledby="worker-work-label" data-invalid={errors.workGroups ? true : undefined}>
                  <label id="worker-work-label">작업</label>
                  <div className="worker-class-controls">
                  
                  <Select aria-label={isRobotDraft ? "운용" : "계약"} value={draft.active ? "on" : "off"} onChange={(e) => setDraft({ ...draft, active: e.target.value === "on" })}>
                    <option value="on">{isRobotDraft ? "운용 유지" : "계약 유지"}</option>
                    <option value="off">{isRobotDraft ? "운용 종료" : "계약 종료"}</option>
                  </Select>
                  {(!isRobotDraft || baseline) && <MultiPicker options={(Object.entries(workerGroupLabels) as Array<[WorkerGroup, string]>).map(([group, label]) => ({ value: group, label }))}
                    value={draft.workGroups} onChange={(next) => setDraft({ ...draft, workGroups: next as WorkerGroup[] })} invalid={!!errors.workGroups} placeholder="작업군" />}
                  {/* 배치 우선순위. 기본은 0 — 올리면 납기보다 먼저 평가되어 그 사람에게 몰아준다. */}
                  <Select aria-label="배치 우선순위" value={String(draft.planningPriority)} onChange={(e) => setDraft({ ...draft, planningPriority: Number(e.target.value) })}>
                    {Array.from({ length: 11 }, (_, value) => <option key={value} value={value}>{value === 0 ? "우선순위 기본" : `우선순위 ${value}`}</option>)}
                  </Select>
                  </div>
                  {errors.workGroups && <span className="ui-field-error" role="alert">{errors.workGroups}</span>}
                </div>
                {/* 사람 하나를 들이는 데 필요한 것이 다 한 묶음이다. 연락·주소·계좌·신분을
                    소제목으로 갈라 두면 묶음마다 제목이 한 줄씩 붙어 창이 길어지는데, 정작
                    그 제목들은 아래 칸 이름이 이미 말하는 것을 되풀이할 뿐이었다. */}
                {!isRobotDraft && <>
                  {/* 2026-10-19 밤 지윤: 은행·계좌번호·예금주명은 한 줄 — claude.ai 「Break reminders」처럼 이름 하나 옆(좁으면 아래)에
                      조작칸 셋이 나란히 선다. 칸마다 이름은 화면 읽기용(aria-label)으로 남긴다. */}
                  <div className="ui-field worker-col-full worker-bank-row" role="group" aria-labelledby="worker-bank-row-label">
                    <label id="worker-bank-row-label">계좌{baseline && editing !== "new" && <span className="ui-field-optional ui-field-optional--mark"> 선택</span>}</label>
                    <div className="worker-bank-controls">
                      <Select aria-label="은행" required={editing === "new"} aria-invalid={errors.bankCode ? true : undefined} value={draft.bankCode} onChange={(e) => setDraft({ ...draft, bankCode: e.target.value, accountNumber: formatAccountNumber(draft.accountNumber, e.target.value) })}><option value="">–</option>{draft.bankCode && !banks.some((bank) => bank.code === draft.bankCode) && <option value={draft.bankCode}>{bankName(draft.bankCode)}</option>}{banks.map((bank) => <option key={bank.code} value={bank.code}>{bank.name}</option>)}</Select>
                      <FormInput aria-label="계좌번호" aria-invalid={errors.accountNumber ? true : undefined} required={editing === "new"} inputMode="numeric" value={draft.accountNumber} onChange={(e) => setDraft({ ...draft, accountNumber: formatAccountNumber(e.target.value, draft.bankCode) })} placeholder="계좌번호" />
                      <FormInput aria-label="예금주명" aria-invalid={errors.accountHolder ? true : undefined} required value={draft.accountHolder} onChange={(e) => setDraft({ ...draft, accountHolder: e.target.value })} placeholder="예금주" />
                    </div>
                    {(errors.bankCode || errors.accountNumber || errors.accountHolder) && <span className="ui-field-error" role="alert">{errors.bankCode ?? errors.accountNumber ?? errors.accountHolder}</span>}
                  </div>
                  {/* 신분증 사진은 주민등록번호 이름표 옆에 붙인다. 둘 다 같은 사실("이
                      사람이 본인인가")을 대는 물건이고, 사진만 따로 칸을 차지하면 이 줄이
                      혼자 두 줄이 된다. 미리보기는 두지 않는다 — 방금 고른 파일을 확인하려고
                      창을 반쯤 채우는 사진을 띄우면 아래 칸들이 전부 밀려난다. */}
                  {/* 2026-10-19 밤 지윤: 주민등록번호도 「계좌」 줄과 같은 짜임 — 이름·설명 왼쪽, 번호 칸과 사진 첨부 단추 오른쪽. */}
                  <div className="ui-field worker-col-full worker-resident-row" role="group" aria-labelledby="worker-resident-label" data-invalid={errors.identityPhoto ? true : undefined}>
                    <label id="worker-resident-label" htmlFor="worker-resident">주민등록번호</label>
                    <div className="worker-resident-controls">
                      <FormInput id="worker-resident" required={editing === "new"} autoComplete="off"
                        aria-invalid={errors.residentRegistrationNumber ? true : undefined}
                        value={draft.residentRegistrationNumber ?? ""}
                        onChange={(e) => setDraft({ ...draft, residentRegistrationNumber: formatResidentNumber(e.target.value) })}
                        placeholder={editing === "new" ? "000000-0000000" : "변경할 때만 입력"} />
                      <input ref={photoInputRef} id="worker-photo-upload" className="worker-photo-input" aria-label="신분증 사진 파일"
                        type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setIdentityPhoto(e.target.files?.[0] ?? null)} />
                      <button type="button" className="worker-photo-chip" onClick={() => photoInputRef.current?.click()}>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7.5h4l1.4-2h5.2l1.4 2h4v11H4z" /><circle cx="12" cy="13" r="3.25" /></svg>
                        {identityPhoto ? "사진 1장" : existing?.identityPhoto ? `사진 ${existing.identityPhotos.length || 1}장` : "사진 첨부"}
                      </button>
                    </div>
                    {(errors.residentRegistrationNumber || errors.identityPhoto) && <span className="ui-field-error" role="alert">{errors.residentRegistrationNumber || errors.identityPhoto}</span>}
                  </div>
                </>}
                {/* 2026-10-19 밤 지윤: 계약 세부는 「계약 기간」·「보수」 두 줄 — 계약(유지·종료)은 구분 아래 그대로 두고,
                    세부는 주민등록번호 다음에 서서 사람·업무위탁·기간제가 위쪽 순서를 똑같이 쓴다. 값은 구분을 바꿔도 그대로다. */}
                {hasContract && <div className="ui-field worker-col-full worker-term-row" role="group" aria-labelledby="worker-term-label">
                  <label id="worker-term-label">계약 기간</label>
                  <div className="worker-term-controls">
                    <FormInput aria-label="계약 시작일" aria-invalid={errors.startDate ? true : undefined} type="date" required value={contractExtra.startDate} onChange={(e) => setContractExtra({ ...contractExtra, startDate: e.target.value })} />
                    <span className="worker-term-sep" aria-hidden="true">~</span>
                    {endDateMode === "none"
                      ? <FormInput aria-label="계약 종료일" disabled value="" placeholder="없음" />
                      : <FormInput aria-label="계약 종료일" aria-invalid={errors.endDate ? true : undefined} type="date" min={contractExtra.startDate} required={endDateMode === "required"} value={contractExtra.endDate}
                          onChange={(e) => setContractExtra({ ...contractExtra, endDate: e.target.value })} />}
                  </div>
                  {(errors.startDate || errors.endDate) && <span className="ui-field-error" role="alert">{errors.startDate ?? errors.endDate}</span>}
                </div>}
                {hasContract && <div className="ui-field worker-col-full worker-pay-row" role="group" aria-labelledby="worker-pay-label">
                  <label id="worker-pay-label">보수</label>
                  <div className="worker-pay-controls">
                    {/* 고를 것이 하나뿐이면(업무위탁 = 계약급) 드롭다운을 세우지 않는다 — 2026-10-19 밤 지윤. */}
                    {wagePeriodChoices.length > 1 && <Select aria-label="보수 주기" value={wagePeriod}
                      onChange={(e) => setContractExtra({ ...contractExtra, wagePeriod: e.target.value as WagePeriod })}>
                      {wagePeriodChoices.map(period => <option key={period} value={period}>{wagePeriodLabels[period]}</option>)}
                    </Select>}
                    <FormInput aria-label="금액" aria-invalid={errors.wage ? true : undefined} inputMode="numeric" disabled={kind === "piece"}
                      value={kind === "piece" ? "" : contractExtra.wage && Number(contractExtra.wage).toLocaleString()}
                      onChange={(e) => setContractExtra({ ...contractExtra, wage: e.target.value.replace(/\D/g, "") })}
                      placeholder={kind === "piece" ? "정산액에 따름" : "0"} />
                  </div>
                  {errors.wage && <span className="ui-field-error" role="alert">{errors.wage}</span>}
                </div>}
              </div>
            </section>
            <section className="worker-profile-section" aria-label="근무 및 배치 정보">
              <div className="worker-profile-grid">
                {/* 단기알바도 같은 칸을 쓴다. 하루만 일하면 계약 시작일과 종료일을 같은 날로
                    적고 그날 요일 하나만 고르면 된다 — 뷰를 나눌 이유가 없었다. */}
                {/* 칩 일곱 개가 한 줄에 서려면 296 이 든다 — 3칸(약 230)에서는 두 줄로 접혔다. */}
                {/* 요일 칩과 요일별 시간 줄은 한 묶음이다 — 저쪽 "방해 금지 시간"과 같은 꼴:
                    칩 줄 아래 16 을 띄우고, 고른 요일마다 줄이 하나씩 선다. */}
                <FormField className="worker-col-full worker-days-field" label="근무 요일" required error={errors.workDays ?? errors.endTime ?? errors.breakMinutes}>
                  <div className="worker-days-stack">
                  {/* 저쪽 "Quiet hours" 처럼 제목 아래 설명 한 줄, 그 아래 16 에 칩. */}
                  <p className="worker-days-desc">고른 요일마다 나오는 시각과 끝나는 시각을 정합니다.</p>
                  <DayChips value={draft.workDays} onChange={(next) => {
                    setDraft({ ...draft, workDays: next });
                    setDayHours(current => Object.fromEntries(next.map(day =>
                      [day, current[day] ?? { start: draft.startTime, end: draft.endTime, rest: restFor(draft.startTime, draft.endTime) }])));
                  }} invalid={!!errors.workDays} />
                  <DayHourRows days={draft.workDays} hours={dayHours}
                    onChange={(day, value) => setDayHours(current => ({ ...current, [day]: value }))} />
                  </div>
                </FormField>
                {/* 누구에게나 묻는다. 대부분 60분으로 같지만 사람마다 다른 공방이 있고, 계획은
                    휴게를 뺀 실근무로 돌아가므로 비워 두면 점심시간까지 일감이 들어간다. */}
                {/* 휴게는 요일 줄마다 고른다(DayHourRows) — 따로 있던 「휴게 (분)」 줄은 걷었다(2026-10-19 밤 지윤). */}
                {isRobotDraft && <FormField className="worker-col-3" label="기본 시간당 생산량" required error={errors.defaultUnitsPerHour}><FormInput type="number" min={0.01} max={1000000} step={0.01} required value={robotExtra.defaultUnitsPerHour} onChange={(e) => setRobotExtra({ ...robotExtra, defaultUnitsPerHour: Number(e.target.value) })} /></FormField>}
                {/* 활성은 계약 안에 두지 않는다. 계약이 없는 구분(도급제·단기알바·로봇)에서는
                    계약 묶음이 통째로 사라지는데, 지금도 일을 맡기느냐는 누구에게나 물어야 한다. */}
                {!isRobotDraft && <div className="worker-col-3 worker-rfid-field">
                  {/* 리더기 상태는 이름 옆에 붙인다. 칸 아래에 한 줄을 더 쓰면 이 칸만
                      길어져 옆 넷과 바닥이 어긋난다. */}
                  <label htmlFor="worker-rfid-tag">RFID 태그 <span className="ui-field-optional ui-field-optional--mark">선택</span>
                    {/* 평소에는 안내를 두지 않는다. 버튼 글씨가 이미 상태를 말하고("리더기 연결"
                        → "연결 중…" → "연결 해제"), 긴 문장을 이름 옆에 붙이면 자리가 없어
                        잘린다. 이 브라우저에서 아예 쓸 수 없을 때만 그 사실을 알린다. */}
                    {rfidSerial.state === "unsupported" && <small id="worker-rfid-status" className={`worker-rfid-status worker-rfid-status--${rfidSerial.state}`} role="status">{rfidSerialLabel}</small>}
                  </label>
                  <div className="worker-rfid-control">
                    <FormInput id="worker-rfid-tag" autoComplete="off" value={draft.rfidTag ?? ""} onChange={(e) => setDraft({ ...draft, rfidTag: e.target.value.toUpperCase() })} placeholder="카드 식별자" aria-describedby="worker-rfid-status" />
                    <FormButton type="button" variant="secondary" size="compact" disabled={rfidSerial.state === "unsupported" || rfidSerial.state === "connecting"} onClick={() => rfidSerial.state === "connected" ? void rfidSerial.disconnect() : void rfidSerial.connect()}>{rfidSerial.state === "connected" ? "연결 해제" : rfidSerial.state === "connecting" ? "연결 중…" : "리더기 연결"}</FormButton>
                  </div>
                </div>}
              </div>
            </section>
          </fieldset>
          {/* 취소는 지운다. 창 오른쪽 위 ×와 Esc가 이미 같은 일을 하고, 나란히 두면 "등록"이
              둘 중 하나를 고르는 일처럼 보인다. */}
          <footer className="worker-profile-actions"><FormButton type="submit" variant="primary" busy={busy}>{busy ? "저장 중…" : editing === "new" ? "등록" : "저장"}</FormButton></footer>
        </form>
      </div>
    </FormModal>}
    {deleting && <Modal busy={busy} className="worker-delete-dialog ui-dialog--confirm" title="작업자 삭제" onClose={() => { if (!busy) { setDeleting(null); setModalMessage(null); } }}>
      <div className="worker-delete-content">
        {modalMessage && <Feedback tone={modalMessage.tone}>{modalMessage.text}</Feedback>}
        <p><strong>{deleting.displayedName}</strong> 작업자를 삭제하시겠습니까?</p>
        <p>작업자 목록과 이후 플래닝 대상에서는 제외되며, 완료한 작업·급여·정산 기록은 그대로 보존됩니다.</p>
        <footer><Button disabled={busy} onClick={() => { setDeleting(null); setModalMessage(null); }}>취소</Button><Button variant="danger" busy={busy} onClick={() => void confirmDelete()}>{busy ? "삭제 중…" : "삭제"}</Button></footer>
      </div>
    </Modal>}
  </>;
}
